import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import { TicketFilter } from "./ticketFilter";
import { selectProfile } from "../auth/authSlice";
import { UserRoleEnum } from "../userRoles/UserRole";
import FormGroup from "../../common/components/form/FormGroup";
import LocationsMultiSelect from "../locations/LocationsMultiSelect";
import TeamsMultiSelect from "../teams/TeamsMultiSelect";
import TicketStatusMultiSelect from "../ticketStatuses/TicketStatusMultiSelect";
import DepartmentsDropdown from "../departments/DepartmentsDropdown";
import UsersDropdown from "../users/UsersDropdown";
import CategoryMultiselect from "../categories/CategoryMultiselect";

interface TicketFilterFormProps {
  filter?: TicketFilter;
  onSubmit: (filter: TicketFilter) => Promise<void>;
}

export default function TicketFilterForm(props: TicketFilterFormProps) {
  const profile = useSelector(selectProfile);
  const isAdmin = profile.role === UserRoleEnum.Admin;
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TicketFilter>({
    values: props.filter,
    defaultValues: {
      departmentId: !isAdmin ? profile.departmentId : props.filter?.departmentId,
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const watchedDepartmentId = watch("departmentId");
  const watchedGrandParentCategoryIds = watch("grandParentCategoryIds");
  const watchedParentCategoryIds = watch("parentCategoryIds");

  useEffect(() => {
    if (props.filter?.departmentId !== watchedDepartmentId) {
      setValue("statusIds", []);
      setValue("locationIds", []);
      setValue("teamIds", []);
      setValue("grandParentCategoryIds", []);
      setValue("parentCategoryIds", []);
      setValue("categoryIds", []);
    }
  }, [watchedDepartmentId]);

  const onSubmit = async (data: TicketFilter) => {
    setIsLoading(true);
    await props.onSubmit(data);
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid">
        <FormGroup className={`col-12 md:${!isAdmin ? "col-6" : "col-4"}`} size="small" label="Nume *" errorMessage={errors.name?.message}>
          <InputText
            placeholder="Adauga nume"
            {...register("name", {
              required: "Numele este obligatoriu",
              validate: (value) => value.trim() !== "" || "Numele este obligatoriu",
            })}
          />
        </FormGroup>
        {isAdmin && (
          <FormGroup className="col-12 md:col-4" size="small" label="Departament" errorMessage={errors.departmentId?.message}>
            <Controller
              name="departmentId"
              control={control}
              rules={{ required: "Departamentul este obligatoriu" }}
              render={({ field }) => <DepartmentsDropdown value={field.value} onChange={field.onChange} />}
            ></Controller>
          </FormGroup>
        )}
        <FormGroup className={`col-12 md:${!isAdmin ? "col-6" : "col-4"}`} size="small" label="Agent">
          <Controller
            name="agentId"
            control={control}
            render={({ field }) => (
              <UsersDropdown
                showClear
                placeholder="Selecteaza Agent"
                role={UserRoleEnum.Agent}
                value={field.value}
                onChange={(e) => field.onChange(e)}
              />
            )}
          ></Controller>
        </FormGroup>
        <FormGroup className="col-12 md:col-4" size="small" label="Status">
          <Controller
            name="statusIds"
            control={control}
            render={({ field }) => (
              <TicketStatusMultiSelect departmentId={watchedDepartmentId} value={field.value} onChange={(e) => field.onChange(e)} />
            )}
          ></Controller>
        </FormGroup>
        <FormGroup className="col-12 md:col-4" size="small" label="Locatii">
          <Controller
            name="locationIds"
            control={control}
            render={({ field }) => <LocationsMultiSelect value={field.value} onChange={(e) => field.onChange(e)} />}
          ></Controller>
        </FormGroup>
        <FormGroup className="col-12 md:col-4" size="small" label="Grupe">
          <Controller
            name="teamIds"
            control={control}
            render={({ field }) => (
              <TeamsMultiSelect departmentId={watchedDepartmentId} value={field.value} onChange={(e) => field.onChange(e)} />
            )}
          ></Controller>
        </FormGroup>
        {watchedDepartmentId && (
          <>
            <FormGroup className="col-12 md:col-4" size="small" label="Categorie L1">
              <Controller
                name="grandParentCategoryIds"
                control={control}
                render={({ field }) => (
                  <CategoryMultiselect
                    departmentId={watchedDepartmentId}
                    level={0}
                    value={field.value}
                    onChange={(ids) => {
                      field.onChange(ids);
                      setValue("parentCategoryIds", []);
                      setValue("categoryIds", []);
                    }}
                  />
                )}
              />
            </FormGroup>
            <FormGroup className="col-12 md:col-4" size="small" label="Categorie L2">
              <Controller
                name="parentCategoryIds"
                control={control}
                render={({ field }) => (
                  <CategoryMultiselect
                    departmentId={watchedDepartmentId}
                    level={1}
                    restrictToParentIds={watchedGrandParentCategoryIds}
                    value={field.value}
                    onChange={(ids) => {
                      field.onChange(ids);
                      setValue("categoryIds", []);
                    }}
                  />
                )}
              />
            </FormGroup>
            <FormGroup className="col-12 md:col-4" size="small" label="Categorie L3">
              <Controller
                name="categoryIds"
                control={control}
                render={({ field }) => (
                  <CategoryMultiselect
                    departmentId={watchedDepartmentId}
                    level={2}
                    restrictToParentIds={watchedParentCategoryIds}
                    restrictToGrandParentIds={watchedGrandParentCategoryIds}
                    value={field.value}
                    onChange={(ids) => field.onChange(ids)}
                  />
                )}
              />
            </FormGroup>
          </>
        )}
      </div>
      <Button type="submit" className="block ml-auto" label="Salveaza" icon="pi pi-save" loading={isLoading} disabled={isLoading}></Button>
    </form>
  );
}
